import { useEffect, useState } from 'react';
import type { Notice } from '@/lib/intake/useDraft';
import styles from './Toast.module.css';

/**
 * Save notices, in a live region that is always in the page — a region added at
 * the moment it has something to say is often not announced at all. Polite for
 * progress; assertive for a save that failed.
 */
export function Toast({ notice }: { notice: Notice | null }) {
  const [visible, setVisible] = useState<Notice | null>(null);

  useEffect(() => {
    if (notice === null) return undefined;
    const show = setTimeout(() => setVisible(notice), 0);
    // A failure stays until something replaces it; good news goes after a moment.
    const hide = notice.tone === 'danger' ? undefined : setTimeout(() => setVisible(null), 4000);
    return () => {
      clearTimeout(show);
      if (hide) clearTimeout(hide);
    };
  }, [notice]);

  return (
    <>
      <div className={styles.live} role="status" aria-live="polite">
        {visible && visible.tone !== 'danger' ? visible.text : ''}
      </div>
      <div className={styles.live} role="alert" aria-live="assertive">
        {visible && visible.tone === 'danger' ? visible.text : ''}
      </div>
      {visible && (
        <div className={[styles.toast, styles[visible.tone]].join(' ')} aria-hidden="true">
          {visible.text}
        </div>
      )}
    </>
  );
}
